import type { LucideIcon } from "lucide-react";
import {
  Shield,
  Search,
  Bug,
  GitBranch,
  Landmark,
  Siren,
  Building2,
  Scale,
  ShieldAlert,
  Camera,
  Network,
  GraduationCap,
} from "lucide-react";

import type { CctvHighlightContent, WebcamHighlightContent } from "../data/planCatalog";

export type SolutionEntry = {
  id: string;
  icon: LucideIcon;
  iconBg: string;
  iconShadow: string;
  tag: string;
  title: string;
  subtitle: string;
  description: string;
  benefits: string[];
  useCases: string[];
  /** Optional CCTV+ add-on angle for this solution workflow. */
  cctvHighlight?: CctvHighlightContent;
  /** Optional Webcam Capture add-on angle for identity at the desk. */
  webcamHighlight?: WebcamHighlightContent;
  accent: "teal" | "emerald";
  gradient: string;
  borderColor: string;
  pageTitle: string;
  metaDescription: string;
  marketingCta: {
    titleBefore: string;
    titleAccent: string;
    description: string;
  };
};

export const solutions: SolutionEntry[] = [
  {
    id: "dispute",
    icon: Shield,
    iconBg: "bg-teal-600",
    iconShadow: "shadow-teal-500/30",
    tag: "Liability Defense",
    title: "Neutralize Dispute Friction.",
    subtitle:
      "Prove your wealth management analyst set the stop-loss order correctly, stopping costly agency-client finger-pointing.",
    description:
      'In high-stakes financial and professional services, disputes over "who did what and when" can escalate into multi-million dollar litigation. DeskReplay provides timestamped visual evidence accepted as evidence in court that definitively resolves he-said-she-said conflicts before they become legal battles.',
    benefits: [
      "Timestamped, frame-accurate recordings of every critical action",
      "Immutable chain-of-custody logs for admissibility",
      "AI-powered search to find the exact moment within seconds",
      "Exonerate correct procedures, penalize actual deviations",
      "Reduce legal costs with proactive evidence management",
      "Optional session audio on Pro+ preserves meeting and call context synchronized with on-screen actions",
    ],
    useCases: [
      "Wealth management wire instruction verification",
      "Insurance claim processing audit trails",
      "Client mandate execution confirmations",
      "Regulatory reporting accuracy defense",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, searchable branch and premises NVR footage can complement desktop session evidence in liability disputes.",
      useCases: [
        "Slip-and-fall and premises liability claims at retail branches or offices",
        "ATM and lobby incidents correlated with transaction timestamps",
        "Insurance defense packages with chain-of-custody camera export",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture adds proportional identity snapshots at advisor and teller workstations so liability reviews can confirm the logged-in employee was at the keyboard, not only what appeared on screen.",
      useCases: [
        "Wire and mandate disputes when shared branch terminals blur who clicked submit",
        "Wealth desk investigations pairing trade timestamps with desk presence on interval schedules (Pro) or app triggers (Enterprise)",
        "Moonlighting or credential-sharing claims where screen replay alone cannot identify the operator",
        "External USB webcams on dual-monitor trading setups when the built-in camera does not face the user",
      ],
    },
    accent: "teal",
    gradient: "from-teal-900/20 to-teal-900/0",
    borderColor: "border-teal-500/20",
    pageTitle: "Dispute resolution & liability defense",
    metaDescription:
      "Resolve costly finger-pointing with court-admissible, timestamped visual evidence from DeskReplay, built for wealth management, insurance, and regulated workflows.",
    marketingCta: {
      titleBefore: "Ready to end costly ",
      titleAccent: "\"who clicked what\" debates?",
      description:
        "Talk to us about dispute-prone workflows, wires, mandates, and client instructions, where visual proof replaces reconstructed chat logs.",
    },
  },
  {
    id: "forensic",
    icon: Search,
    iconBg: "bg-emerald-600",
    iconShadow: "shadow-emerald-500/30",
    tag: "Forensic Internal Audit",
    title: "Investigate with Tamper-Proof Chain of Custody.",
    subtitle:
      "Conduct security incident investigations with forensic-grade digital evidence that your legal, compliance, and HR teams can trust.",
    description:
      "When a data breach, policy violation, or insider threat occurs, traditional logging tells you what happened. DeskReplay shows you exactly what the operator saw, did, and bypassed. Every pixel, every action, every bypass is captured with immutable metadata.",
    benefits: [
      "Visual evidence for HR disciplinary proceedings",
      "Immutable audit logs for every recording access and deletion",
      "Secure evidence package export for external counsel",
      "Automated anomaly flagging when SOP deviations occur",
      "Chain-of-custody documentation from capture to courtroom",
      "Optional session audio profiles on Pro and Enterprise for synchronized voice and screen replay",
    ],
    useCases: [
      "Insider threat investigations",
      "Data exfiltration forensics",
      "Compliance breach documentation",
      "Regulatory examinations and audits",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, NVR streams can be indexed into the same searchable, exportable archive as desktop recordings for forensic investigations.",
      useCases: [
        "Insider theft or after-hours access paired with workstation playback",
        "Chain-of-custody ZIP export for counsel covering camera and desktop evidence",
        "Cross-camera timeline reconstruction during HR or security reviews",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture supplements insider threat and HR forensics with timed JPEG snapshots at the endpoint, confirming physical presence when logs and screen replay show policy bypass but not who sat at the desk.",
      useCases: [
        "Data exfiltration reviews when the suspect account was active but presence is disputed",
        "Shared forensic lab or review stations where wrong-person logins matter",
        "Enterprise trigger snapshots when high-risk apps or export tools gain focus",
        "Chain-of-custody packages that pair desktop replay with identity stills for counsel",
      ],
    },
    accent: "emerald",
    gradient: "from-emerald-900/20 to-emerald-900/0",
    borderColor: "border-emerald-500/20",
    pageTitle: "Forensic audit & insider investigations",
    metaDescription:
      "Tamper-evident visual recordings and chain-of-custody exports for HR, legal, and security investigations, with DeskReplay self-hosted on your infrastructure.",
    marketingCta: {
      titleBefore: "Ready for evidence that ",
      titleAccent: "survives scrutiny?",
      description:
        "We'll walk through retention, RBAC, and export paths so investigations finish faster, with fewer privileged back-and-forths.",
    },
  },
  {
    id: "rca",
    icon: Bug,
    iconBg: "bg-teal-600",
    iconShadow: "shadow-teal-500/30",
    tag: "Root Cause Analysis",
    title: "Give developers the ultimate bug report.",
    subtitle:
      "Capture the exact state of the computer at that moment of system or CAD design failure, eliminating guesswork entirely.",
    description:
      '"Works on my machine" is the most expensive phrase in enterprise software. DeskReplay captures the exact visual and application state at the moment of failure, giving your engineering teams a perfect replication scenario every time.',
    benefits: [
      "Visual state capture at exact failure timestamp",
      "Application-scoped recording (record only relevant software)",
      "AI transcription of all on-screen text and UI elements",
      "Searchable archives for recurring issue pattern detection",
      "Reduce MTTR by up to 80% with visual context",
    ],
    useCases: [
      "CAD/CAM design failure analysis",
      "ERP transaction errors in pharma/manufacturing",
      "QA reproduction of intermittent bugs",
      "Legacy system failure documentation",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, RCA teams can pair workstation capture with indexed NVR streams to see both the screen and the physical environment.",
      useCases: [
        "Manufacturing line stoppages with synchronized HMI and floor camera playback",
        "Construction or field equipment incidents at job sites",
        "Warehouse automation faults with aisle and dock camera context",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture documents which engineer or operator was at the failure workstation during RCA windows, paired with application-scoped screen capture at the exact timestamp.",
      useCases: [
        "CAD and ERP reproduction sessions when multiple specialists share a validation PC",
        "Manufacturing HMI incidents where the batch record user ID does not match floor reality",
        "QA bug bashes confirming who reproduced the defect on a shared test bench",
        "Startup snapshots (Enterprise) after sign-in on overnight batch monitoring laptops",
      ],
    },
    accent: "teal",
    gradient: "from-teal-900/20 to-teal-900/0",
    borderColor: "border-teal-500/20",
    pageTitle: "Root cause analysis & engineering reproduction",
    metaDescription:
      "Cut MTTR with frame-accurate visual replay at failure time, DeskReplay gives QA and SRE teams the bug report operators actually needed.",
    marketingCta: {
      titleBefore: "Ready to retire ",
      titleAccent: "\"cannot reproduce\"?",
      description:
        "See how application-scoped capture pairs with search so engineering stops debating hypotheses and starts watching truth.",
    },
  },
  {
    id: "sop",
    icon: GitBranch,
    iconBg: "bg-emerald-600",
    iconShadow: "shadow-emerald-500/30",
    tag: "Process Transparency",
    title: "Absolute visibility into your remote workforce.",
    subtitle:
      "Gain visibility into remote, offshore, or outsourced teams. Produce proof that teams follow the approved process without manual oversight.",
    description:
      "As enterprise operations scale globally with remote and outsourced teams, process integrity becomes impossible to guarantee without evidence. DeskReplay provides passive, policy-driven monitoring that helps demonstrate SOP (Standard Operating Procedure) adherence to reviewers without intrusive surveillance.",
    benefits: [
      "Policy-driven recording (trigger only on defined applications)",
      "SOP deviation alerting via AI behavior analysis",
      "Offshore team compliance verification dashboards",
      "Non-invasive design: screen-first capture; optional session audio on Pro and Enterprise when policy permits",
      "Integration with existing HRIS and compliance systems",
    ],
    useCases: [
      "Offshore financial data processing teams",
      "Remote pharma data entry verification",
      "Outsourced legal document processing",
      "Ad-ops campaign trafficking oversight",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, NVR streams at processing centers can add physical floor context alongside application-scoped desktop capture.",
      useCases: [
        "Offshore data entry floors: desktop golden-path plus lobby and aisle camera review",
        "After-hours access at shared service centers",
        "Client QBR evidence packages combining screen replay and facility cameras",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture helps verify golden-path adherence on remote, hybrid, and offshore desks by confirming the person at the keyboard matches the account under review.",
      useCases: [
        "Offshore processing floors with hot-desking and seat-sharing programs",
        "Remote pharma data entry where credential sharing violates client contracts",
        "Random interval checks (Pro) across shifts without recording every idle minute",
        "Wrong-person investigations when VPN logs show the correct user but presence is questioned",
      ],
    },
    accent: "emerald",
    gradient: "from-emerald-900/20 to-emerald-900/0",
    borderColor: "border-emerald-500/20",
    pageTitle: "SOP adherence & remote workforce assurance",
    metaDescription:
      "Evidence to support golden-path adherence reviews for outsourced and remote teams, policy-triggered visual capture without ambient surveillance.",
    marketingCta: {
      titleBefore: "Ready to verify ",
      titleAccent: "golden-path reality?",
      description:
        "We'll map capture policies to the apps and transaction classes that matter, so leadership sees proof, not dashboards full of assumptions.",
    },
  },
  {
    id: "regulatory",
    icon: Landmark,
    iconBg: "bg-teal-600",
    iconShadow: "shadow-teal-500/30",
    tag: "Regulatory & Exams",
    title: "Survive examinations with evidence, not anecdotes.",
    subtitle:
      "When a regulator, enterprise customer, or major accounting firm audit asks how a control was exercised on a specific date, chat logs alone rarely suffice.",
    description:
      "DeskReplay preserves screen recordings scoped to the apps that matter so compliance and legal teams can demonstrate what operators saw and did during controlled workflows. Exports carry evidence-handling records attached to every export, suitable for examiner requests and internal quality investigations.",
    benefits: [
      "Timestamped visual reconstruction of regulated workflows (where capture policies permit)",
      "Immutable access logs for reviewer activity on sensitive archives",
      "Segmentation between routine retention and legal-hold buckets",
      "Faster responses to questionnaires that demand proof of human execution, not only system logs",
      "Maps cleanly to programs that already maintain SOX, GxP, or ISO-style evidence expectations",
    ],
    useCases: [
      "SOx-style control testing and external audit samples",
      "GxP laboratory or manufacturing workstation verification",
      "Insurance carrier or reinsurer operational audits",
      "Customer contractual audit clauses for outsourced processing",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, indexed NVR footage can meet examiner requests for physical environment proof with the same retention discipline as desktop capture.",
      useCases: [
        "GxP manufacturing floor camera samples alongside batch workstation review",
        "SOX-style control walkthroughs that include secure area access footage",
        "Customer audit clauses covering outsourced sites with camera archives",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture supports examiner and customer audit questions about who executed a control on a specific date, not only what the system logged on screen.",
      useCases: [
        "SOX and GxP walkthroughs sampling human execution at validated workstations",
        "Outsourced processing sites where client auditors require identity evidence with screen replay",
        "Proportionate interval schedules for teller and lab operator desks during exam windows",
        "Enterprise triggers on regulated applications when control narratives demand event-linked identity",
      ],
    },
    accent: "teal",
    gradient: "from-teal-900/20 to-teal-900/0",
    borderColor: "border-teal-500/20",
    pageTitle: "Regulatory examinations & audit defense",
    metaDescription:
      "Demonstrate human execution of controls with policy-scoped visual trails, built for SOX, GxP, and customer audit clauses.",
    marketingCta: {
      titleBefore: "Ready for examiner questions about ",
      titleAccent: "who did what on screen?",
      description:
        "We'll align capture scopes to your control narratives so questionnaires get playback-backed answers, not reconstructed threads.",
    },
  },
  {
    id: "incident",
    icon: Siren,
    iconBg: "bg-emerald-600",
    iconShadow: "shadow-emerald-500/30",
    tag: "SOC & Incident Response",
    title: "Give incident command the same picture analysts had on glass.",
    subtitle:
      "Bridge SIEM alerts and ticketing with visual truth about what analysts saw on their screens during browser sessions, consoles, and internal tools during an active investigation.",
    description:
      "Traditional artifacts explain that an account executed a command or visited a URL; DeskReplay clarifies context that analysts saw when making irreversible decisions. Integrate exports into incident timelines while preserving RBAC (Role-Based Access Control) so only cleared responders access recordings.",
    benefits: [
      "Correlate detection timestamps with operator-visible UI state",
      "Reduce back-and-forth between SOC and business owners about intent",
      "Package sequences for legal review under privilege workflows where applicable",
      "Support tabletop exercises with realistic playback instead of synthetic narratives",
      "Optional transcription and semantic search to locate relevant spans quickly",
      "System audio streaming on Pro aligns analyst playbacks with softphone and meeting audio",
    ],
    useCases: [
      "Phishing or BEC investigations involving internal workstations",
      "Malware triage when operators interacted with suspicious prompts",
      "Privilege misuse reviews coordinated with HR and counsel",
      "Customer-facing breach narratives backed by defensible evidence",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, IR teams can correlate analyst desktops with live NVR streams for physical-layer context during active incidents.",
      useCases: [
        "Tailgating or unauthorized entry alerts while triaging account compromise",
        "War-room timelines that include both analyst screen replay and camera feeds",
        "Post-incident packages for legal with cross-camera search results",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture adds identity context on analyst workstations during active IR, confirming who was at the console when irreversible triage actions occurred on screen.",
      useCases: [
        "Phishing and BEC war rooms when multiple analysts rotate through the same jump host",
        "Privilege misuse reviews pairing SIEM timestamps with desk presence stills",
        "SOC shift handoffs where unattended consoles are a physical security concern",
        "Enterprise capture when the case management or EDR console gains focus during sev-1 events",
      ],
    },
    accent: "emerald",
    gradient: "from-emerald-900/20 to-emerald-900/0",
    borderColor: "border-emerald-500/20",
    pageTitle: "SOC & incident response visual evidence",
    metaDescription:
      "Align detections with what analysts saw, DeskReplay adds operator-visible context to SIEM timelines for IR and breach narratives.",
    marketingCta: {
      titleBefore: "Ready when alerts fire at ",
      titleAccent: "3 a.m.?",
      description:
        "We'll show how SOC RBAC, retention, and export packages fit your war-room and legal review workflows.",
    },
  },
  {
    id: "vendor",
    icon: Building2,
    iconBg: "bg-teal-600",
    iconShadow: "shadow-teal-500/30",
    tag: "Vendor & Outsourcing",
    title: "Extend your standard of proof to BPO and offshore desks.",
    subtitle:
      "Hold contractors to the same standard of proof as internal teams without always-on recording of every idle minute.",
    description:
      "Automatic rules activate capture when outsourced teams touch regulated applications or transaction classes you define. Leadership gains assurance that SLAs and scripts were followed during critical processing windows while respecting jurisdictional privacy obligations negotiated in the main vendor contract.",
    benefits: [
      "Application-scoped capture aligned to contractual scopes of work",
      "Evidence exports for vendor disputes or service-credit negotiations",
      "Objective inputs for quarterly business reviews with outsourcers",
      "Supports hybrid models where some workflows remain onshore only",
      "Clear separation between customer data environments via tenant isolation",
    ],
    useCases: [
      "Offshore loan underwriting or document preparation",
      "Claims intake and adjusting support centers",
      "Shared service centers spanning HR, finance, or procurement tools",
      "Transition periods when migrating work between vendors",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Enterprise Plugin, you can index NVR streams at outsourced sites for SLA and security oversight beyond desktop capture.",
      useCases: [
        "Per-client camera fleets for MSPs managing multiple BPO locations",
        "Incident queues and monthly CSV reports for client QBRs",
        "Physical security alerts at high-risk processing centers",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture adds timed identity snapshots at outsourced workstations so client and vendor reviewers can confirm the logged-in user matches the person at the keyboard, paired with contract-scoped screen evidence.",
      useCases: [
        "Shared-seat and hot-desk BPO floors where account sharing violates client MSAs",
        "Moonlighting and wrong-person investigations when offshore replay shows the correct user ID only",
        "Client QBR packages pairing script adherence with Pro interval or Enterprise trigger identity checks",
        "External USB webcams on agent pods where the built-in laptop camera faces the wrong direction",
      ],
    },
    accent: "teal",
    gradient: "from-teal-900/20 to-teal-900/0",
    borderColor: "border-teal-500/20",
    pageTitle: "Vendor, BPO & outsourcing assurance",
    metaDescription:
      "Contract-scoped visual evidence for outsourced desks, prove SLA adherence and resolve disputes without recording idle time.",
    marketingCta: {
      titleBefore: "Ready to audit vendors like ",
      titleAccent: "insiders?",
      description:
        "We'll tune triggers to contractual scopes so offshore teams get oversight, not ambient surveillance.",
    },
  },
  {
    id: "workplace",
    icon: Scale,
    iconBg: "bg-emerald-600",
    iconShadow: "shadow-emerald-500/30",
    tag: "Workplace Integrity",
    title: "Ground sensitive inquiries in facts, not memory.",
    subtitle:
      "Where law and policy permit monitoring, preserve tamper-evident context for complaints involving electronic communications or on-screen conduct.",
    description:
      "DeskReplay is not a substitute for POSH committees, qualified investigators, or mandatory policies issued under the Sexual Harassment of Women at Workplace Act and rules. It is a technical layer: policy-scoped capture, secure retention, and AI-assisted review (such as semantic or pattern-assisted triage) help authorized teams locate relevant material faster while proportionality, notice, and consent remain decisions for HR and counsel.",
    benefits: [
      "Evidence-grade exports when investigations are cleared to review workstation activity",
      "Semantic search and transcription to narrow relevant intervals (subject to policy)",
      "Immutable audit trails showing who accessed sensitive archives",
      "System audio on Pro captures client-side call audio without a separate recorder stack",
      "Supports coordinated workflows with committee administrators and external advisors under instruction",
      "Reduces reliance on screenshots that are trivially altered or incomplete",
      "Enterprise mic plus system audio profiles preserve disputed verbal exchanges alongside on-screen conduct",
    ],
    useCases: [
      "Allegations involving explicit imagery or harmful messaging via corporate channels",
      "Clarifying whether instructions or approvals appeared on screen during a disputed interaction",
      "Demonstrating adherence to acceptable-use policies during disciplinary proceedings",
      "Pairing with dedicated POSH training and grievance processes mandated by statute",
    ],
    cctvHighlight: {
      summary:
        "Where policy permits and you purchase the optional CCTV+ Plugin, workplace investigations can extend to indexed common-area and parking NVR streams.",
      useCases: [
        "Parking lot and lobby incidents reviewed alongside workstation context",
        "Proportionate camera profiles that limit which AI modules run per feed",
        "Immutable logs of who accessed sensitive camera segments during an inquiry",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture provides proportionate identity evidence during lawful workplace inquiries when allegations involve who was present at a shared, remote, or hybrid desk.",
      useCases: [
        "POSH and conduct investigations where on-screen messaging is disputed alongside presence",
        "Shared HR or intake PCs where wrong-person logins undermine findings",
        "Remote hybrid workers when policy permits verifying the assigned employee at the keyboard",
        "Pairing Visual Policy or conduct-related screen segments with identity stills for committee review",
      ],
    },
    accent: "emerald",
    gradient: "from-emerald-900/20 to-emerald-900/0",
    borderColor: "border-emerald-500/20",
    pageTitle: "Workplace integrity & lawful investigations",
    metaDescription:
      "Policy-scoped, tamper-evident workstation context for HR and counsel-led inquiries, proportionate capture aligned with POSH and acceptable-use programs.",
    marketingCta: {
      titleBefore: "Ready for investigations grounded in ",
      titleAccent: "tamper-evident context?",
      description:
        "We'll discuss lawful scopes, retention, and reviewer RBAC so authorized teams move faster, without crossing proportionality lines.",
    },
  },
  {
    id: "content-safety",
    icon: ShieldAlert,
    iconBg: "bg-teal-600",
    iconShadow: "shadow-teal-500/30",
    tag: "Content Safety",
    title: "Content Safety and Policy Compliance.",
    subtitle:
      "Automated AI scanning catches explicit, harmful, and policy-violating content before a single manual review is needed. One occurrence is enough.",
    description:
      "Manual review of hours of recorded footage is impossible at scale. One undetected incident involving explicit content, weapons, illegal or restricted substances, political material, or harassment imagery can create legal, HR, or regulatory liability overnight. DeskReplay runs purpose-built visual AI in the background, scanning every recorded segment automatically. Admins define policy rules once and the system alerts immediately on the first occurrence, no pattern required.",
    benefits: [
      "Every recorded segment scanned automatically: no manual review queue to build or maintain",
      "NSFW Detection with 96.5% accuracy on real images: one occurrence triggers an instant alert",
      "Visual Policy Rule Engine: define unlimited custom rules using typed concepts or reference image uploads",
      "Catches weapons, controlled substances, gambling, political and religious content, harassment imagery, and competitor material",
      "Policy Alerts Dashboard for security and HR teams: triage flagged segments, mark as reviewed, filter by rule, severity, or endpoint",
      "Single-occurrence alerting: nothing is missed until a pattern is detected",
      "Full audit trail: every alert and review action logged for compliance and legal hold",
    ],
    useCases: [
      "NSFW and explicit content detection on corporate devices",
      "Weapons and controlled substance monitoring for regulated sites",
      "Political and religious content enforcement for publicly regulated organizations",
      "Harassment imagery detection for HR and workplace integrity programs",
      "Confidential document leak detection and gaming or gambling during work hours",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, the same visual policy engine that scans desktop recordings can run on indexed camera segments for lobby, warehouse, and public-area feeds.",
      useCases: [
        "NSFW and weapons detection on camera footage at regulated facilities",
        "Policy alerts triaged in the same admin console as desktop violations",
        "Per-camera profiles that scope which checks run on each NVR stream",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture pairs desktop Visual Policy alerts with endpoint identity snapshots so security and HR can tell account sharing apart from a single user violating content rules.",
      useCases: [
        "NSFW or weapons policy hits when investigators need to confirm who was at the device",
        "Shared kiosks and hot desks in regulated or public-facing back offices",
        "Moonlighting scenarios where prohibited content appears under a legitimate employee login",
        "Enterprise triggers when flagged applications open alongside first-occurrence policy alerts",
      ],
    },
    accent: "teal",
    gradient: "from-teal-900/20 to-teal-900/0",
    borderColor: "border-teal-500/20",
    pageTitle: "Content safety and visual policy compliance",
    metaDescription:
      "Automated AI scanning for NSFW content, weapons, substances, political material, and custom policy rules: single-occurrence alerting with a full audit trail for HR and compliance teams.",
    marketingCta: {
      titleBefore: "Ready for automated policy ",
      titleAccent: "enforcement at scale?",
      description:
        "We will walk through the Visual Policy Rule Engine, the Alerts Dashboard, and how on-device AI keeps all footage inside your infrastructure.",
    },
  },
  {
    id: "process-coaching",
    icon: GraduationCap,
    iconBg: "bg-teal-600",
    iconShadow: "shadow-teal-500/30",
    tag: "Training Intelligence",
    title: "Turn Your Best Performers Into a Living Training Library.",
    subtitle:
      "Stop coaching from memory. Let your top employees show the way through recordings of their actual work.",
    description:
      "DeskReplay captures how your best operators handle the same workflows your new hires struggle with. Managers can pull up timestamped recordings of exemplary sessions, navigate directly to the key moments using AI search, and share them as structured onboarding or coaching reference material. All from the same admin console you already use for compliance and audit. No extra tooling, no unofficial recording tools, no guesswork about what good actually looks like.",
    benefits: [
      "Cut time to become productive by showing new hires the exact steps top performers take, not a slide deck approximation",
      "AI-powered search surfaces exemplary sessions in seconds: find every recording where the correct SOP (Standard Operating Procedure) steps were followed",
      "Session playback with speed control and OCR panel lets coaches annotate and navigate to the teaching moment precisely",
      "Consistent onboarding across remote, offshore, and outsourced teams from a single archive strong enough for compliance",
      "Identify performance patterns across the team: who follows process, who improvises, and what actually drives outcomes",
      "No additional software required: the same recording infrastructure that supports compliance also powers coaching",
    ],
    useCases: [
      "CRM and transaction workflow onboarding for new financial services hires",
      "Call centre coaching using recordings of top-performing agent interactions",
      "SOP excellence demos for pharma and regulated data-entry teams",
      "High-performer pattern capture for remote and offshore quality programs",
      "Manager-led coaching sessions where screen evidence replaces memory",
    ],
    webcamHighlight: {
      summary:
        "Optional Webcam Capture can confirm trainees and coaches were at the assigned workstation during exemplary sessions archived for onboarding, without turning coaching into always-on surveillance.",
      useCases: [
        "Remote onboarding when managers need proof the new hire performed the demo, not a buddy on the same login",
        "Offshore quality programs comparing golden-path screen replay with desk presence on sample intervals",
        "Call center coaching rooms using external USB webcams on shared training PCs",
        "Enterprise startup snapshots on loaner laptops issued for certification weeks",
      ],
    },
    accent: "teal",
    gradient: "from-teal-900/20 to-teal-900/0",
    borderColor: "border-teal-500/20",
    pageTitle: "Process coaching & training intelligence",
    metaDescription:
      "Turn recorded desktop sessions of top performers into structured training material. DeskReplay lets managers find exemplary sessions with AI search and share them as coaching reference material across the team.",
    marketingCta: {
      titleBefore: "Ready to turn great work into ",
      titleAccent: "a repeatable standard?",
      description:
        "We will show you how AI search surfaces exemplary sessions and how managers share them as coaching material from the same console used for compliance.",
    },
  },
  {
    id: "cctv-surveillance",
    icon: Camera,
    iconBg: "bg-teal-600",
    iconShadow: "shadow-teal-500/30",
    tag: "CCTV AI Surveillance",
    title: "Turn Camera Footage into Searchable Evidence.",
    subtitle:
      "Security teams scrub through hours of footage manually after incidents. Real-time alerts go unnoticed because nobody watches every feed simultaneously.",
    description:
      "DeskReplay's CCTV+ Plugin indexes camera footage automatically, fires hot-path alerts within 2 seconds of detection, and lets investigators search across all cameras with plain-English queries. From footage to forensics in seconds, without sending a single frame to a third-party cloud.",
    benefits: [
      "Hot path alerts in under 2 seconds from live NVR stream analysis",
      "Cold path deep indexing: OCR, visual search, NSFW screening, scene analytics",
      "AI narrative summaries for every 5-minute footage segment",
      "Cross-camera timeline reconstruction with plain-English search",
      "Forensic-grade ZIP export with SHA-256 chain-of-custody manifest",
      "All AI runs on-premise, your infrastructure, your footage, zero cloud egress",
    ],
    useCases: [
      "Retail shrinkage investigation and staff policy compliance",
      "Premises liability disputes and slip-and-fall evidence",
      "Guard performance monitoring and immobility detection",
      "Access control audit trails and unauthorized zone entry alerts",
      "Bank branch and ATM lobby incident reconstruction",
      "Warehouse dock and yard damage claims in logistics",
      "Insurance SIU property loss site camera search",
    ],
    webcamHighlight: {
      summary:
        "Optional Webcam Capture complements CCTV+ NVR intelligence by verifying the operator at the VMS or monitoring workstation is physically present, deterring unattended consoles while wall cameras watch the facility.",
      useCases: [
        "Control room attendance during overnight shifts when immobility alerts fire on wall feeds",
        "Confirm the logged-in VMS user matches the analyst at the multi-monitor desk",
        "External USB webcams on SOC benches where built-in laptop cameras do not face the operator",
        "Post-incident timelines pairing hot-path CCTV+ alerts with operator identity snapshots",
        "Enterprise triggers when the VMS, PSIM, or incident ticketing app gains focus during sev-1 events",
      ],
    },
    accent: "teal",
    gradient: "from-teal-900/20 to-teal-900/0",
    borderColor: "border-teal-500/20",
    pageTitle: "CCTV AI surveillance and footage intelligence",
    metaDescription:
      "AI-powered CCTV surveillance with hot path alerts under 2 seconds, cold path deep indexing, cross-camera search, and forensic export. Self-hosted, no cloud egress.",
    marketingCta: {
      titleBefore: "Ready to go from ",
      titleAccent: "footage to forensics in seconds?",
      description:
        "We will walk through NVR integration, alert configuration, and how the dual-engine pipeline fits your existing infrastructure.",
    },
  },
  {
    id: "physical-security-msp",
    icon: Network,
    iconBg: "bg-emerald-600",
    iconShadow: "shadow-emerald-500/30",
    tag: "MSP Physical Security",
    title: "Manage Every Client's Camera Infrastructure from One Dashboard.",
    subtitle:
      "MSPs managing 20+ client sites have no unified view of incidents across properties. Onboarding a new client with 40 cameras takes days of manual entry.",
    description:
      "Hierarchical multi-tenancy, bulk camera CSV import, per-client incident queues, and automated reporting let one MSP team manage hundreds of cameras across dozens of client sites with the CCTV+ Enterprise add-on. Switch between client contexts in one click, deliver chain-of-custody reports on demand.",
    benefits: [
      "Bulk CSV camera import: onboard 40 cameras in minutes, not days",
      "Per-client forensic export with tamper-evident chain-of-custody ZIP",
      "MSP fleet dashboard with per-client alert counts, health status, and ingest lag",
      "Per-client incident queue with acknowledgment, assignment, and SLA tracking",
      "Automated CSV incident reports for monthly client deliverables",
      "Org switcher to move between client contexts without re-authenticating",
    ],
    useCases: [
      "Contracted security firms managing retail and commercial properties",
      "Facilities management companies with multi-building portfolios",
      "Property management groups requiring unified incident reporting",
      "Smart building operators with mixed camera vendor deployments",
    ],
    webcamHighlight: {
      summary:
        "Optional Webcam Capture lets MSPs extend managed security deliverables with endpoint identity checks at customer guard desks and SOCs, separate from the CCTV+ camera fleets you operate per tenant.",
      useCases: [
        "Client SOC workstations: verify operators stay at the monitoring station during contracted hours",
        "Guard force desks with shared logins where wrong-person access must be ruled out quickly",
        "Monthly client reports that combine CCTV+ incident queues with proportionate operator presence samples",
        "USB webcam rollouts at client sites where desk layout requires a dedicated camera assignment",
      ],
    },
    accent: "emerald",
    gradient: "from-emerald-900/20 to-emerald-900/0",
    borderColor: "border-emerald-500/20",
    pageTitle: "MSP physical security management",
    metaDescription:
      "MSP physical security platform with hierarchical multi-tenancy, bulk camera CSV import, per-client forensic export, and fleet dashboard. Built for managed security providers.",
    marketingCta: {
      titleBefore: "Ready to manage your entire ",
      titleAccent: "camera fleet from one place?",
      description:
        "We will scope the MSP tenancy model, bulk import workflow, and per-client reporting to your existing service delivery process.",
    },
  },
];

export function getSolutionBySlug(slug: string | undefined): SolutionEntry | undefined {
  if (!slug) return undefined;
  return solutions.find((s) => s.id === slug);
}
